import { useEffect, useState } from 'react'
import { legalApi } from '@carrent/api'

/**
 * Строка согласия с правовыми документами.
 *
 * Значения по умолчанию продублированы на клиенте намеренно. Текст
 * согласия стоит под кнопкой входа, и если запрос за настройками
 * не дошёл, показать экран без упоминания оферты хуже, чем показать
 * его с названиями по умолчанию: юридически значимо само уведомление.
 *
 * Тексты документов сюда не входят: они весят десятки килобайт
 * и грузятся отдельно, когда пользователь открывает документ.
 */
export const LEGAL_FALLBACK = {
  user_agreement_title: 'Пользовательского соглашения',
  privacy_policy_title: 'Политики конфиденциальности',
  consent_prefix: 'Нажимая на кнопку, я соглашаюсь с условиями',
  enabled: true,
}

/**
 * Настройки живут дольше одного экрана и меняются раз в год.
 * Модульный кэш избавляет от повторного запроса при переходе
 * между входом и регистрацией.
 */
let cache = null
let inflight = null

/** Кэш текстов документов: key → { title, text }. */
let docCache = {}

export function resetLegalCache() {
  cache = null
  inflight = null
  docCache = {}
}

/**
 * Текст правового документа для чтения внутри приложения.
 * key — 'user_agreement' | 'privacy_policy'.
 *
 * Ошибка не глотается, в отличие от fetchLegal: там уместен запасной
 * вариант, здесь пользователь явно попросил документ и должен увидеть
 * состояние ошибки с кнопкой «Повторить», а не пустоту.
 */
export async function fetchLegalDocument(key) {
  if (docCache[key]) return docCache[key]
  const { data } = await legalApi.getDocument(key)
  docCache[key] = data.data
  return docCache[key]
}

export async function fetchLegal() {
  if (cache) return cache
  if (!inflight) {
    inflight = legalApi
      .get()
      .then(({ data }) => {
        cache = { ...LEGAL_FALLBACK, ...(data.data || {}) }
        return cache
      })
      .catch(() => LEGAL_FALLBACK)
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

export function useLegal() {
  const [legal, setLegal] = useState(cache || LEGAL_FALLBACK)
  const [loading, setLoading] = useState(!cache)

  useEffect(() => {
    let alive = true
    fetchLegal().then((value) => {
      if (!alive) return
      setLegal(value)
      setLoading(false)
    })
    return () => {
      alive = false
    }
  }, [])

  return { legal, loading }
}
